import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { encodeTime } from '@campfire/hot-date';
import { DateTime } from 'luxon';
import { useApiUrl } from '../../../../global/config/useApiUrl';
import { useCampfireFetch } from '../../../../global/network/useCampfireFetch';
import { useSnackbar } from '../../../../global/config/useSnackbar';
import { BulkAttendanceVolunteer } from './BulkCheckInDialog';
import { CICODateSelect } from '../../../general/vol-dashboard/vol-dashboard-my-elements/main-section/my-activities/CICO/CICODateSelect';

interface CheckedInEntry {
  volunteerId: string;
  CICOId: string;
  activityName: string;
  sessionName: string;
  checkIn: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  volunteers: BulkAttendanceVolunteer[];
  onComplete: () => void;
}

export function BulkCheckOutDialog({ open, onClose, volunteers, onComplete }: Props) {
  const apiUrl = useApiUrl();
  const { run } = useCampfireFetch({ defer: true });
  const { setSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [checkedIn, setCheckedIn] = useState<CheckedInEntry[]>([]);
  // CICOId -> chosen check-out time ('' means "Now", resolved at submit)
  const [checkOutTime, setCheckOutTime] = useState<Record<string, Date | ''>>({});
  // time shown on the "set for all" control
  const [bulkTime, setBulkTime] = useState<Date | ''>('');

  const applyTimeToAll = (value: Date) => {
    setBulkTime(value);
    setCheckOutTime(() => {
      const next: Record<string, Date | ''> = {};
      checkedIn.forEach((c) => {
        next[c.CICOId] = value;
      });
      return next;
    });
  };

  const nameFor = useMemo(() => {
    const map: Record<string, string> = {};
    volunteers.forEach((v) => {
      map[v.volunteerId] = v.name;
    });
    return map;
  }, [volunteers]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    run({
      method: 'post',
      url: `${apiUrl}/vm/session/cico/attendance-options`,
      data: {
        volunteerIds: volunteers.map((v) => v.volunteerId),
        activityDate: DateTime.local().toFormat('yyyy-MM-dd'),
      },
    })
      .then((response) => {
        setCheckedIn(response?.data?.data?.checkedIn ?? []);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = () => {
    if (checkedIn.length === 0) return;
    const checkOutItems = checkedIn.map((c) => {
      const chosen = checkOutTime[c.CICOId];
      return {
        CICOId: c.CICOId,
        checkOut: chosen ? encodeTime(DateTime.fromJSDate(chosen)) : encodeTime(DateTime.local()),
      };
    });

    setSubmitting(true);
    run({
      method: 'post',
      url: `${apiUrl}/vm/session/cico/checkout`,
      data: { checkOutItems },
    })
      .then((response) => {
        if (response.ok) {
          setSnackbar({ open: true, message: `Checked out ${checkOutItems.length} volunteer(s)`, variant: 'success' });
          onComplete();
          onClose();
        } else {
          setSnackbar({ open: true, message: 'Failed to check out volunteers', variant: 'error' });
        }
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle disableTypography style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography style={{ fontWeight: 600, fontSize: '16px' }}>Check Out Volunteers</Typography>
        <IconButton size='small' onClick={onClose}>
          <CloseIcon fontSize='small' />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers style={{ minHeight: 160 }}>
        {loading ? (
          <Box display='flex' justifyContent='center' padding={4}>
            <CircularProgress size={24} />
          </Box>
        ) : checkedIn.length === 0 ? (
          <Typography variant='body2' style={{ textAlign: 'center', color: '#999', padding: '24px' }}>
            None of the selected volunteers are currently checked in.
          </Typography>
        ) : (
          <>
            <Typography variant='body2' style={{ marginBottom: '12px', color: '#464646' }}>
              {checkedIn.length} of {volunteers.length} selected are currently checked in.
            </Typography>
            <Box
              display='flex'
              alignItems='center'
              justifyContent='space-between'
              style={{ padding: '4px 0 12px', borderBottom: '1px solid #f0f0f0', marginBottom: '4px', gap: '12px' }}
            >
              <Typography variant='body2' style={{ fontWeight: 600, color: '#464646' }}>
                Set check-out time for all
              </Typography>
              <CICODateSelect field='bulk-checkout-all' time={bulkTime} onChange={applyTimeToAll} />
            </Box>
            {checkedIn.map((c) => (
              <Box
                key={c.CICOId}
                display='flex'
                alignItems='center'
                justifyContent='space-between'
                style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0', gap: '12px' }}
              >
                <Typography style={{ fontSize: '14px', fontWeight: 500, flex: 1 }}>
                  {nameFor[c.volunteerId] ?? 'Volunteer'}
                </Typography>
                <Box display='flex' alignItems='center' justifyContent='flex-end' style={{ gap: '8px' }}>
                  <Typography style={{ fontSize: '13px', color: '#646464', textAlign: 'right' }}>
                    {c.activityName} — {c.sessionName}
                  </Typography>
                  <CICODateSelect
                    field={`checkout-${c.CICOId}`}
                    time={checkOutTime[c.CICOId] ?? ''}
                    onChange={(value) => setCheckOutTime((prev) => ({ ...prev, [c.CICOId]: value }))}
                  />
                </Box>
              </Box>
            ))}
          </>
        )}
      </DialogContent>
      <DialogActions style={{ padding: '12px 16px' }}>
        <TabletButton variant='text' onClick={onClose} disabled={submitting}>
          Cancel
        </TabletButton>
        <TabletButton
          variant='contained'
          color='primary'
          onClick={handleSubmit}
          disabled={checkedIn.length === 0 || submitting}
          style={{ color: '#fff' }}
        >
          {submitting ? <CircularProgress size={20} /> : `Check Out (${checkedIn.length})`}
        </TabletButton>
      </DialogActions>
    </Dialog>
  );
}
